import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import { CEP } from 'cep-promise';
import { useEffect, useState } from 'react';
import { Controller, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '~/components/Input';
import InputDate from '~/components/InputDate';
import { DocumentType } from '~/constants/documentType';
import { useContractLoader } from '~/hooks/useContractLoader';
import { ContractPostAddress } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { searchAddressByZipcode } from '~/utils/searchAddressByZipcode';
import { CONTRACT_JOURNEY_APP_IDS, contractStepField } from '../contractAppIds';
import { contractPath } from '../buildContractSearchParams';
import ContractJourneyShell from '../components/ContractJourneyShell';
import ContractStepCard from '../components/ContractStepCard';
import { step1CombinedSchema, type Step1FormValues } from '../validation/step1CombinedSchema';

function formatPhoneForMask(phone: string): string {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length >= 10) {
    const ddd = digits.slice(0, 2);
    const first = digits.slice(2, 7);
    const second = digits.slice(7, 11);
    return `(${ddd}) ${first}-${second}`;
  }
  return phone || '';
}

export default function ContractStep1Page() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [useBuyerData, setUseBuyerData] = useState(true);

  const { contract_id } = useContractLoader({ stepName: 'contract_journey_step1' });

  const { contractAsyncPostCustomer, contractAsyncPostAddress, quote, insured, setZipcode } = useStore((store) => ({
    contractAsyncPostCustomer: store.contractAsyncPostCustomer,
    contractAsyncPostAddress: store.contractAsyncPostAddress,
    quote: store.quote,
    insured: store.insured,
    setZipcode: store.setZipcode
  }));

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    getValues,
    formState: { errors, dirtyFields }
  } = useForm<Step1FormValues>({
    resolver: yupResolver(step1CombinedSchema) as Resolver<Step1FormValues>,
    defaultValues: {
      socialName: '',
      name: '',
      mobile_phone: '',
      document: '',
      birthDate: '',
      email: '',
      confirm_email: '',
      password: '',
      confirm_password: '',
      additional_details: '',
      city: '',
      country: '',
      neighborhood: '',
      number: '',
      state: '',
      street: '',
      zipcode: ''
    }
  });

  const opts = { shouldValidate: true, shouldDirty: true };

  const applyBuyerData = (enabled: boolean) => {
    if (!enabled) {
      reset();
      return;
    }

    const hasInsuredData = insured?.name || insured?.document || insured?.email || insured?.mobile_phone;
    if (hasInsuredData) {
      setValue('socialName', insured?.social_name || '', opts);
      setValue('name', insured?.name ?? '', opts);
      setValue('mobile_phone', formatPhoneForMask(insured?.mobile_phone || ''), opts);
      setValue('document', insured?.document ?? '', opts);
      setValue('birthDate', insured?.birthdate ?? '', opts);
      setValue('email', insured?.email ?? '', opts);
      setValue('confirm_email', insured?.email ?? '', opts);
    } else if (quote?.name || quote?.cpf || quote?.email) {
      setValue('socialName', '', opts);
      setValue('name', quote.name ?? '', opts);
      setValue('mobile_phone', formatPhoneForMask(quote.phoneNumber || ''), opts);
      setValue('document', quote.cpf ?? '', opts);
      setValue('birthDate', quote.birthDate ?? '', opts);
      setValue('email', quote.email ?? '', opts);
      setValue('confirm_email', quote.email ?? '', opts);
    }

    const address = insured?.address;
    const hasInsuredAddress = address?.zipcode || address?.street || address?.city || address?.state;
    if (hasInsuredAddress && address) {
      setValue('city', address.city ?? '', opts);
      setValue('state', address.state ?? '', opts);
      setValue('neighborhood', address.neighborhood ?? '', opts);
      setValue('street', address.street ?? '', opts);
      setValue('zipcode', address.zipcode ?? '', opts);
      setValue('number', address.number ?? '', opts);
      setValue('additional_details', address.additional_details ?? '', opts);
      return;
    }

    if (quote?.zipcode) {
      setValue('zipcode', quote.zipcode, opts);
      setValue('city', quote.city ?? '', opts);
      setValue('state', quote.state ?? '', opts);
      setValue('neighborhood', quote.neighborhood ?? '', opts);
      setValue('street', quote.street ?? '', opts);
      setValue('number', quote.number ?? '', opts);
      setValue('additional_details', '', opts);
    }
  };

  const handleChangeUseBuyerData = () => {
    const next = !useBuyerData;
    setUseBuyerData(next);
    applyBuyerData(next);
  };

  const handleChangeZipCode = (value: string) => {
    const cleanZipCode = value.replaceAll('-', '').trim();
    const onSuccess = (params: CEP) => {
      setValue('city', params.city);
      setValue('state', params.state);
      setValue('neighborhood', params.neighborhood);
      setValue('street', params.street);
      setValue('zipcode', params.cep);
    };
    const onError = () => {
      storeState.addToast({
        title: 'Não foi possível encontrar o CEP',
        message: 'O CEP indicado não foi encontrado',
        type: 'warning'
      });
    };
    if (cleanZipCode.length === 8) {
      searchAddressByZipcode(value, onSuccess, onError);
    }
  };

  const onSubmit: SubmitHandler<Step1FormValues> = async (data) => {
    if (!contract_id) {
      storeState.addToast({
        title: 'Não foi possível recuperar o contrato.',
        message: '',
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    const orgParam = searchParams.get('org');

    try {
      await contractAsyncPostCustomer(
        contract_id,
        {
          name: data.name.trim(),
          social_name: data.socialName?.trim(),
          birthDate: data.birthDate,
          email: data.email.trim(),
          mobile_phone: data.mobile_phone,
          document: data.document,
          document_type: DocumentType.CPF,
          password: data.password
        },
        orgParam ?? undefined
      );

      const addressPayload: ContractPostAddress = {
        street: data.street.trim(),
        number: String(data.number),
        additional_details: data.additional_details?.trim() ?? '',
        neighborhood: data.neighborhood.trim(),
        city: data.city.trim(),
        state: data.state.trim(),
        zipcode: data.zipcode,
        country: 'Brasil'
      };

      await contractAsyncPostAddress(contract_id, addressPayload, orgParam ?? undefined);
      setZipcode(getValues('zipcode'));
      navigate(contractPath('/contract/equipment', searchParams));
    } catch (error: unknown) {
      logErrorWithContext(error, 'contract_journey_step1', { contractId: contract_id });
      const errorResponse = getErrorMessage(error);
      storeState.addToast(errorResponse);
      reportError(`Erro ao salvar dados do contrato (passo 1): ${errorResponse.title}`, error, {
        context: 'contract_journey_step1',
        contractId: contract_id
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (useBuyerData) applyBuyerData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insured, quote]);

  return (
    <ContractJourneyShell
      appId={CONTRACT_JOURNEY_APP_IDS.step1}
      // Do passo 1 o «Voltar» sai do contrato: passa pelo aviso «Lembre-se de que somente finalizamos…».
      backLeavesJourney
      onBack={() => navigate(-1)}
      progressPercent={67}
    >
      <form data-app-id={`${CONTRACT_JOURNEY_APP_IDS.step1}-form`} onSubmit={handleSubmit(onSubmit)}>
        <ContractStepCard
          actionsAppIdPrefix={CONTRACT_JOURNEY_APP_IDS.step1}
          loading={loading}
          onPrimary={() => {}}
          stepLabel="Passo 1 de 3"
          title="Informações do segurado"
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2
            }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useBuyerData}
                    color="primary"
                    data-app-id={contractStepField(1, 'use-buyer-data')}
                    onChange={handleChangeUseBuyerData}
                  />
                }
                label="Utilizar as mesmas informações do titular da compra."
              />
            </FormGroup>

            <Typography variant="h6">Dados pessoais</Typography>

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'name')}
                  label="Nome completo"
                  placeholder="Seu nome completo"
                  error={Boolean(errors.name)}
                  isDirty={dirtyFields.name}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="mobile_phone"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'mobile-phone')}
                  label="Celular"
                  mask="phone"
                  placeholder="(DDD) 9 9999-9999"
                  error={Boolean(errors.mobile_phone)}
                  isDirty={dirtyFields.mobile_phone}
                  helperText={errors.mobile_phone?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="document"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'document')}
                  label="CPF"
                  mask="cpf"
                  placeholder="000.000.000-00"
                  error={Boolean(errors.document)}
                  isDirty={dirtyFields.document}
                  helperText={errors.document?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="birthDate"
              control={control}
              render={({ field }) => (
                <InputDate
                  appId={contractStepField(1, 'birth-date')}
                  label="Data de nascimento"
                  placeholder="DD/MM/AAAA"
                  error={Boolean(errors.birthDate)}
                  isDirty={dirtyFields.birthDate}
                  helperText={errors.birthDate?.message}
                  setField={(value) => setValue('birthDate', value)}
                  {...field}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'email')}
                  label="E-mail"
                  placeholder="Seu e-mail para cadastro de login"
                  error={Boolean(errors.email)}
                  isDirty={dirtyFields.email}
                  helperText={errors.email?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="confirm_email"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'confirm_email')}
                  label="Confirmar E-mail"
                  placeholder="Confirme seu e-mail para cadastro de login"
                  error={Boolean(errors.confirm_email)}
                  isDirty={dirtyFields.confirm_email}
                  helperText={errors.confirm_email?.message}
                  {...field}
                />
              )}
            />

            <Typography variant="h6">Senha de acesso ao app</Typography>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'password')}
                  label="Senha"
                  revealable
                  type="password"
                  placeholder="Senha de acesso"
                  error={Boolean(errors.password)}
                  isDirty={dirtyFields.password}
                  helperText={errors.password?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="confirm_password"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'confirm-password')}
                  label="Confirmar senha"
                  revealable
                  type="password"
                  placeholder="Confirmar senha"
                  error={Boolean(errors.confirm_password)}
                  isDirty={dirtyFields.confirm_password}
                  helperText={errors.confirm_password?.message}
                  {...field}
                />
              )}
            />

            {/* Figma 1680:7113: título no mesmo nível das outras secções + subtítulo menor. */}
            <Box data-app-id={contractStepField(1, 'address-heading')}>
              <Typography variant="h6">Endereço completo</Typography>
              <Typography color="text.secondary" variant="body2">
                Onde o equipamento fica guardado
              </Typography>
            </Box>

            <Controller
              name="zipcode"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(1, 'zipcode')}
                  label="CEP"
                  mask="zipcode"
                  placeholder="XXXXX-XXX"
                  error={Boolean(errors.zipcode)}
                  isDirty={dirtyFields.zipcode}
                  helperText={errors.zipcode?.message}
                  {...field}
                  onChange={(event) => {
                    handleChangeZipCode(event.target.value);
                    field.onChange(event);
                  }}
                />
              )}
            />
            <Grid container spacing={2}>
              <Grid item md={8} xs={12}>
                <Controller
                  name="street"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'street')}
                      label="Endereço"
                      placeholder="Digite primeiro o CEP"
                      error={Boolean(errors.street)}
                      isDirty={dirtyFields.street}
                      helperText={errors.street?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Controller
                  name="number"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'number')}
                      label="Número"
                      placeholder="Número da residência"
                      error={Boolean(errors.number)}
                      isDirty={dirtyFields.number}
                      helperText={errors.number?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Controller
                  name="neighborhood"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'neighborhood')}
                      label="Bairro"
                      placeholder="Digite primeiro o CEP"
                      error={Boolean(errors.neighborhood)}
                      isDirty={dirtyFields.neighborhood}
                      helperText={errors.neighborhood?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name="additional_details"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'additional-details')}
                      label="Complemento"
                      placeholder="Apto ou bloco"
                      error={Boolean(errors.additional_details)}
                      isDirty={dirtyFields.additional_details}
                      helperText={errors.additional_details?.message}
                      {...field}
                      value={field.value ?? ''}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'state')}
                      label="Estado/ UF"
                      inputProps={{ maxLength: 2 }}
                      placeholder="UF"
                      error={Boolean(errors.state)}
                      isDirty={dirtyFields.state}
                      helperText={errors.state?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Input
                      appId={contractStepField(1, 'city')}
                      label="Cidade"
                      placeholder="Digite primeiro o CEP"
                      error={Boolean(errors.city)}
                      isDirty={dirtyFields.city}
                      helperText={errors.city?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </ContractStepCard>
      </form>
    </ContractJourneyShell>
  );
}
